<script>
import AppIcon from '~/components/UI/app-icon/AppIcon.vue'

export default {
  name: 'AppButton',

  components: {
    AppIcon,
  },

  props: {
    type: {
      type: String,
      default: 'button',
      validator: (value) => ['button', 'submit'].includes(value),
    },
    variant: {
      type: String,
      default: 'secondary',
      validator: (value) =>
        ['primary', 'secondary', 'yellow', 'white', 'mulberry', 'red', 'old-gray', 'icon'].includes(value),
    },
    size: {
      type: String,
      default: 'large',
      validator: (value) => ['small', 'medium', 'large'].includes(value),
    },
    icon: {
      type: Boolean,
      default: false,
    },
    text: {
      type: Boolean,
      default: false,
    },
    underline: {
      type: Boolean,
      default: false,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    loading: {
      type: Boolean,
      default: false,
    },
    prependIcon: {
      type: String,
      default: '',
    },
    appendIcon: {
      type: String,
      default: '',
    },
    block: {
      type: Boolean,
      default: false,
    },
    to: {
      type: String,
      default: '',
    },
    fontClass: {
      type: String,
      default: 'font-14-500',
    },
    noOutline: {
      type: Boolean,
      default: false,
    },
    textAlign: {
      type: String,
      default: 'center',
      validator: (value) => ['center', 'left', 'right'].includes(value),
    },
    target: {
      type: String,
      default: '_self',
      validator: (value) => ['_self', '_blank'].includes(value),
    },
    dataElement: {
      type: String,
      default: '',
    },
  },

  emits: ['click', 'prepend-icon-click', 'append-icon-click'],

  computed: {
    modifications() {
      const mods = [
        { icon: this.icon },
        { text: this.text },
        { disabled: this.disabled },
        { loading: this.loading },
        { underline: this.underline },
        { block: this.block },
        { 'no-outline': this.noOutline },
        `text-align-${this.textAlign}`,
        this.fontClass,
      ]

      if (!this.text) {
        mods.push(`variant-${this.variant}`)
        mods.push(`size-${this.size}`)
      }

      return mods
    },

    component() {
      if (this.to?.startsWith('http')) {
        return 'a'
      }

      return this.to ? 'router-link' : 'button'
    },

    attrs() {
      const attrs = {}

      if (this.disabled || this.loading) {
        attrs.event = null
        attrs['aria-disabled'] = true
      }

      if (this.to?.startsWith('http')) {
        attrs.href = this.to
        attrs.target = '_blank'

        return attrs
      } else {
        attrs.to = this.to
        attrs.target = this.target
        return attrs
      }
    },
  },
}
</script>

<template>
  <component
    :is="component"
    class="app-button"
    :class="modifications"
    :disabled="disabled || loading"
    :type="type"
    v-bind="attrs"
    :data-element="dataElement"
    @click="$emit('click', $event)"
  >
    <span class="app-button-wrapper">
      <app-icon
        v-if="prependIcon"
        class="app-button-prepend-icon"
        :name="prependIcon"
        size="related"
        @click="$emit('prepend-icon-click', $event)"
      />
      <slot />
      <app-icon
        v-if="appendIcon"
        class="app-button-append-icon"
        :name="appendIcon"
        size="related"
        @click="$emit('append-icon-click', $event)"
      />
    </span>
  </component>
</template>

<style lang="scss" scoped>
.app-button {
  --text-color: transparent;
  --border-color: transparent;
  --bg-color: transparent;

  position: relative;
  display: inline-flex;
  align-items: center;
  padding: 0 24px;
  border: 1px solid var(--border-color);
  border-radius: 6px;
  background-color: var(--bg-color);
  color: var(--text-color);
  font-family: 'General Sans', sans-serif;
  font-weight: 600;
  text-decoration: none;
  cursor: pointer;
  transition: opacity 0.2s, background-color 0.2s, color 0.2s;

  &:focus-visible {
    outline: 2px solid var(--neutral-1);
  }

  &.text-align-left {
    justify-content: flex-start;
  }

  &.text-align-center {
    justify-content: center;
  }

  &.text-align-right {
    justify-content: flex-end;
  }

  &:where(.size-small) {
    &:not(.variant-icon) {
      height: 36px;
      min-height: 36px;
      font-size: 14px;
      line-height: 0;
    }

    &.variant-icon {
      width: 24px;
      height: 24px;
      padding: 4px;
      border-radius: 8px;
      font-size: 16px;
    }
  }

  &:where(.size-medium) {
    &:not(.variant-icon) {
      height: 44px;
      min-height: 44px;
      font-size: 14px;
      line-height: 0;
    }

    &.variant-icon {
      width: 36px;
      height: 36px;
      padding: 8px;
      border-radius: 12px;
      font-size: 20px;
    }
  }

  &:where(.size-large) {
    height: 52px;
    min-height: 52px;
    font-size: 16px;
    line-height: 0;
  }

  &.block {
    width: 100%;
  }

  &:where(.font-14-400, .font-14-400-tu, .font-15-400, .font-16-400, .font-12-400) {
    font-weight: 400;
  }

  &:where(.font-14-500) {
    font-weight: 500;
  }

  &:where(
    .font-10-500,
    .font-12-500,
    .font-14-500,
    .font-14-500-i,
    .font-15-500,
    .font-16-500,
    .font-18-500,
    .font-22-500,
    .font-26-500,
    .font-32-500,
    .font-60-500
  ) {
    font-weight: 500;
  }

  &:where(.font-14-700, .font-15-700, .font-18-700, .font-22-700, .font-26-700, .font-28-700) {
    font-weight: 700;
  }

  &:disabled &-wrapper {
    cursor: not-allowed;
  }

  &-wrapper {
    display: flex;
    align-items: center;
    gap: 8px;
    line-height: 1em;
  }

  &:where(.variant-primary) {
    --border-color: var(--secondary-1);
    --bg-color: var(--secondary-1);
    --text-color: #fff;

    &:hover {
      opacity: 0.7;
    }

    &.disabled {
      --border-color: transparent;
      --bg-color: var(--secondary-1);
      --text-color: #fff;

      opacity: 0.55;
    }
  }

  &:where(.variant-secondary) {
    --border-color: var(--neutral-5);
    --bg-color: transparent;
    --text-color: var(--neutral-2);

    box-shadow: 0 0 0 1px var(--neutral-6);

    &:hover {
      opacity: 0.7;
    }

    &.disabled {
      --border-color: var(--neutral-5);
      --text-color: var(--neutral-4);

      opacity: 1;
    }
  }

  &:where(.variant-yellow) {
    --border-color: var(--secondary-2);
    --bg-color: var(--secondary-2);
    --text-color: #212529;

    &:hover {
      opacity: 0.7;
    }

    &.disabled {
      --border-color: var(--neutral-6);
      --bg-color: var(--neutral-6);
      --text-color: var(--neutral-4);

      opacity: 1;
    }
  }

  &:where(.variant-white) {
    --border-color: var(--neutral-8-30);
    --bg-color: var(--neutral-8-30);
    --text-color: #fff;

    &:hover {
      --border-color: var(--neutral-8-50);
      --bg-color: var(--neutral-8-50);
    }

    &.disabled {
      --text-color: var(--neutral-8-50);

      opacity: 1;
    }
  }

  &:where(.variant-red) {
    --border-color: var(--secondary-3);
    --bg-color: var(--secondary-3);
    --text-color: #fff;

    &:hover {
      opacity: 0.8;
    }

    &.disabled {
      --border-color: var(--secondary-3-25);
      --bg-color: var(--secondary-3-25);

      opacity: 0.55;
    }
  }

  &:where(.variant-mulberry) {
    --border-color: transparent;
    --bg-color: var(--mulberry);
    --text-color: #fff;

    &:hover {
      --bg-color: var(--mulberry-light);
    }

    &.disabled {
      --bg-color: #dcd3fb;
    }

    &:focus-visible {
      outline: 3px solid var(--lilac);
    }
  }

  &:where(.variant-old-gray) {
    --border-color: var(--neutral-6);
    --bg-color: var(--neutral-6);
    --text-color: var(--neutral-1);

    &:hover {
      --bg-color: var(--neutral-5);
    }

    &.disabled {
      --bg-color: var(--neutral-7);
      --text-color: var(--neutral-4);
    }

    &:focus-visible {
      outline: 3px solid var(--lilac);
    }
  }

  &:where(.variant-icon) {
    --border-color: transparent;
    --bg-color: transparent;
    --text-color: var(--neutral-1);

    &:hover {
      background-color: var(--neutral-6);
    }

    &.disabled {
      background-color: var(--neutral-6);
      opacity: 0.6;
    }
  }

  &:where(.text) {
    --border-color: transparent;
    --bg-color: transparent;
    --text-color: var(--primary-cold);

    width: auto;
    height: auto;
    padding: 0;
    box-shadow: none;

    &:hover {
      opacity: 0.8;
    }

    &:disabled,
    &.disabled {
      opacity: 0.5;
    }

    &.underline {
      text-decoration: underline;
    }
  }

  &:where(.icon) {
    --border-color: transparent;
    --bg-color: transparent;
    --text-color: inherit;

    margin: 0;
    width: 32px;
    min-width: auto;
    height: 32px;
    min-height: auto;
    padding: 0;
    border-radius: 100%;
    box-shadow: none;
  }

  &.disabled {
    cursor: not-allowed;
  }

  &.no-outline {
    --border-color: transparent;

    box-shadow: none;
    outline: none;
  }

  &.loading {
    color: transparent;
    cursor: not-allowed;

    &::before {
      position: absolute;
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%);
      display: inline-block;
      width: 16px;
      height: 16px;
      border: 2px solid var(--text-color);
      border-radius: 50%;
      animation: spin-animation 1s linear infinite;
      border-bottom-color: transparent;
      content: '';
    }
  }

  &-prepend-icon,
  &-append-icon {
    width: 16px;
    color: inherit;
    font-size: 16px;
  }
}
</style>

<style>
@keyframes spin-animation {
  100% {
    transform: translate(-50%, -50%) rotate(360deg);
  }
}
</style>
